<script lang="tsx">
import momentDurationFormatSetup from "moment-duration-format"
import {defineComponent, reactive, toRefs, watch} from "vue";
// A default import, not "import * as moment": moment is CommonJS, and webpack's interop handed the
// namespace object straight through as the moment function. Under real ESM semantics the namespace is
// { default: moment, ... }, so moment.duration was undefined and the setup call below threw
// "Cannot set properties of undefined (setting 'format')", which took the whole app down on boot.
import moment from "moment";
import {AppStore} from "@/store";
import ToastProvider from '@/components/common/toast/ToastProvider.vue';
import ModalProvider from "@/components/common/modal/ModalProvider.vue";
import "@/utils/array"
import "@/utils/json"
import "@/utils/set"
import "@/utils/files"
import {useRoute} from "vue-router";
import {getCurrentUser, setOptOut, isUserAnonymous, User} from "@/services/user";
import {getPendingPodcastsCount} from "@/services/podcasts";
import NotificationProvider from "@/components/common/notification/NotificationProvider.vue";
import AddToHomeScreen from "@/components/pwa/AddToHomeScreen.vue"; 

momentDurationFormatSetup(moment)

const loadScript = (src: string) => {
  const script: HTMLScriptElement = document.createElement("script");
  script.src = src;
  script.async = true;
  document.head.appendChild(script);
};

const loadConsentScript = (user: User) => {
  if (isUserAnonymous(user)) {
    document.body.classList.add("fluidWebRoot") // set for dark theme in consent layer
    loadScript(process.env.CONSENT_SCRIPT_URL ?? "")
  }
};


function loadTrackingScripts(user: User) {
  loadScript("//w3.siemens.com/ote/ote_config.js");
  if (isUserAnonymous(user)) {
    loadScript("//static.dc.siemens.com/scripts/ote/global/ote.js");
  } else {
    loadScript("//static.dc.siemens.com/scripts/ote/sinet/ote.js");
  }
}

export default defineComponent({
  name: 'App',
  components: {NotificationProvider, ModalProvider, ToastProvider, AddToHomeScreen},
  setup() {

    const {loading} = AppStore.provideStore({
      initState: async () => {
        const user = await getCurrentUser();
        const state = reactive({
          appTitle: "Siemens.FM",
          user,
          transactionId: document.transactionId,
          pendingApprovals: user.profile.isApprover ? await getPendingPodcastsCount() : undefined
        });

        watch(toRefs(state.user.profile).optOut, (value) => {
          setOptOut(value)
        })

        loadConsentScript(user);
        loadTrackingScripts(user);

        return state;
      },
      methods: {}
    })

    const route = useRoute();

    return () =>
      !loading.value &&
      <div class="app">
        <div id="top"/>
        <AddToHomeScreen/>
        <ToastProvider>
          <ModalProvider>
            <NotificationProvider>
              <router-view/>
            </NotificationProvider>
          </ModalProvider>
        </ToastProvider>
      </div>
  }
});

</script>

<style lang="scss" scoped>
@import "src/css/common/index";

.app {
  background: $color-surface;
  color: $color-brand-2;
  min-height: 100vh;
}
</style>

